import axios from 'axios';
import { Device, Alert, User, DeviceData } from '../types';
import { API_BASE_URL, handleApiError } from './_apiBase';

export const fetchDevices = async (): Promise<Device[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Hämta enheter');
    }
};

export const fetchDeviceData = async (deviceId: string): Promise<DeviceData[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/data/${deviceId}`);
        return response.data;
    } catch (error) {
        return handleApiError(error, `Fetching data for device ${deviceId}`);
    }
};

export const controlDevice = async (deviceId: string, action: string): Promise<any> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices/${deviceId}/control`, { action });
        return response.data;
    } catch (error) {
        return handleApiError(error, `Controlling device ${deviceId}`);
    }
};

export const fetchUsers = async (): Promise<User[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/users`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Hämta användare');
    }
};

export const fetchDeviceStatuses = async (): Promise<Device[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Hämta enheternas läge');
    }
};

export const fetchAlerts = async (): Promise<Alert[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/alerts`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Hämta larm');
    }
};

export const fetchDeviceStatus = async (deviceId: string): Promise<string> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${deviceId}`);
        return response.data.status || 'okänd';
    } catch (error) {
        return handleApiError(error, `Fetching status for device ${deviceId}`);
    }
};

export const fetchDevice = async (deviceId: string): Promise<Device> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/devices/${deviceId}`);
        return response.data;
    } catch (error) {
        return handleApiError(error, `Fetching device ${deviceId}`);
    }
};

export const updateDeviceStatus = async (deviceId: string, status: string): Promise<any> => {
    try {
        const response = await axios.put(`${API_BASE_URL}/devices/${deviceId}/status`, { status });
        return response.data;
    } catch (error) {
        return handleApiError(error, `Updating status for device ${deviceId}`);
    }
};

export const fetchData = async (endpoint: string): Promise<any[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}${endpoint}`);
        return response.data;
    } catch (error) {
        return handleApiError(error, `Fetching data from ${endpoint}`);
    }
};

export const addDevice = async (device: Omit<Device, 'id'>): Promise<Device> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/devices`, device);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Lägga till enhet');
    }
};

/**
 * Update device (name, type, etc.)
 */
export const updateDevice = async (deviceId: string, updates: Partial<Device>): Promise<Device> => {
    try {
        const response = await axios.put(`${API_BASE_URL}/devices/${deviceId}`, updates);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Spara enhet');
    }
};

/**
 * Delete/remove a device
 */
export const deleteDevice = async (deviceId: string): Promise<{ success: boolean; message: string }> => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/devices/${deviceId}`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'Ta bort enhet');
    }
};

/**
 * Enable or disable a device. When disabled the coordinator ignores all incoming messages.
 */
export const setDeviceDisabled = async (deviceId: string, disabled: boolean): Promise<{ message: string; disabled: boolean }> => {
    try {
        const response = await axios.patch(`${API_BASE_URL}/devices/${deviceId}/disabled`, { disabled });
        return response.data;
    } catch (error) {
        return handleApiError(error, `Setting disabled=${disabled} for device ${deviceId}`);
    }
};

/**
 * Delete all historical data (readings & parameters) for a device, keeping the device itself.
 */
export const deleteDeviceData = async (deviceIeee: string): Promise<{ success: boolean; message: string }> => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/data/${deviceIeee}`);
        return response.data;
    } catch (error: any) {
        return handleApiError(error, 'Ta bort enhetens data');
    }
};
